import { useMemo } from "react";

import { useSelector } from "react-redux";

import { useFiltersValueDeltas } from "app/pages/sales-intelligence/modules/advanced-search-filtering/hooks/useFiltersValueDeltas";
import { StyledActionsWrapper } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/lead-generator/components/message-strategy/styles";
import {
    type LeadsSearchAllFiltersTypes,
    selectDirtyFilters,
} from "app/pages/sales-intelligence/modules/leads-search-filters";
import { leadsFilterDtoToPanelValue } from "app/pages/sales-intelligence/modules/leads-search-filters/utils/leadsFilterDtoAdapters";
import type { IPlainObject } from "app/pages/sales-intelligence/types/plainObject";

import { FiltersStateStrategy, type FiltersStateStrategyProps } from "../../components/message-strategy";
import type { FiltersStateData } from "../../types/content";
import type { FiltersStateSettings } from "../../types/models";
import SavedSearchActionsContainer from "../SavedSearchActionsContainer/SavedSearchActionsContainer";

export type FiltersStateStrategyContainerProps<T extends IPlainObject = IPlainObject> = Pick<
    FiltersStateStrategyProps<T>,
    "filtersManager" | "onFilterUpdate" | "onFilterValueRemove"
> & {
    filtersStateData: FiltersStateData;
    settings: FiltersStateSettings;
};

const FiltersStateStrategyContainer = ({
    settings,
    filtersManager,
    filtersStateData,
    onFilterUpdate,
    onFilterValueRemove,
}: FiltersStateStrategyContainerProps<LeadsSearchAllFiltersTypes>) => {
    const dirtyFilters = useSelector(selectDirtyFilters);

    const filtersStateList = useMemo(() => leadsFilterDtoToPanelValue(filtersStateData), [filtersStateData]);

    const filterValueDeltaList = useFiltersValueDeltas<LeadsSearchAllFiltersTypes>({
        filtersList: filtersStateList,
        filtersManager,
        currentFiltersValue: dirtyFilters,
    });

    return (
        <>
            <FiltersStateStrategy
                isFilterActionEnabled={settings.isFilterActionEnabled}
                filtersList={filtersStateList}
                filtersManager={filtersManager}
                filterValueDeltaList={filterValueDeltaList}
                onFilterUpdate={onFilterUpdate}
                onFilterValueRemove={onFilterValueRemove}
            />

            {settings.isSaveFiltersEnabled ? (
                <StyledActionsWrapper>
                    <SavedSearchActionsContainer filtersManager={filtersManager} />
                </StyledActionsWrapper>
            ) : null}
        </>
    );
};

export default FiltersStateStrategyContainer;
